import { API_URL, getAccessToken } from '@/api/client';

/** The build that is running (the Docker build sets it to the git commit). */
export const RELEASE: string = import.meta.env.VITE_RELEASE || 'dev';

const MAX_PER_PAGE = 10;
const sent = new Set<string>();

export interface ReportableError { kind: string; message: string; stack?: string | null; componentStack?: string | null }

/** Errors that say nothing about the app: cross-origin scripts, browser extensions, layout loops, cancelled requests. */
export function isNoise(e: ReportableError): boolean {
  const m = e.message.trim();
  if (!m || m === 'Script error.' || m === 'Script error') return true;
  if (m.startsWith('ResizeObserver loop')) return true;
  if (e.kind === 'AbortError' || m.includes('The user aborted a request')) return true;
  return /(chrome|moz|safari-web)-extension:\/\//.test(e.stack ?? '');
}

/** Turns whatever was thrown into something to send. */
export function describe(error: unknown): ReportableError {
  if (error instanceof Error) return { kind: error.name || 'Error', message: error.message, stack: error.stack ?? null };
  if (typeof error === 'string') return { kind: 'Error', message: error };
  try {
    return { kind: 'Error', message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { kind: 'Error', message: String(error) };
  }
}

/**
 * Sends an error to the platform admins' error list (Yönetim → Hatalar). The same error is sent once per page load, at most ten in all;
 * sending never throws.
 */
export function reportError(error: unknown, extra: { componentStack?: string | null } = {}): void {
  const e = { ...describe(error), componentStack: extra.componentStack ?? null };
  if (isNoise(e)) return;
  const key = `${e.kind}|${e.message}|${(e.stack ?? '').split('\n')[1] ?? ''}`;
  if (sent.has(key) || sent.size >= MAX_PER_PAGE) return;
  sent.add(key);
  const token = getAccessToken();
  try {
    void fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        kind: e.kind.slice(0, 200), message: e.message.slice(0, 1000), stack: e.stack?.slice(0, 6000) ?? null, url: window.location.href,
        release: RELEASE, componentStack: e.componentStack?.slice(0, 2000) ?? null,
      }),
    }).catch(() => undefined);
  } catch {
    // reporting must never add a second failure
  }
}

/** Reports uncaught errors and rejected promises, and reloads once when a new deploy removed the code this page was built with. */
export function installErrorReporting(): void {
  window.addEventListener('error', (event) => {
    if (event.error) reportError(event.error);
    else if (event.message) reportError({ name: 'Error', message: event.message, stack: event.filename ? `at ${event.filename}:${event.lineno}:${event.colno}` : undefined });
  });
  window.addEventListener('unhandledrejection', (event) => reportError(event.reason));
  // After a deploy, an open page asks for chunks that no longer exist: load the new version instead of failing (once per minute at most).
  window.addEventListener('vite:preloadError', (event) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem('testrome.reloadedForUpdate') ?? 0);
    } catch { /* storage may be blocked */ }
    if (Date.now() - last < 60_000) return;
    event.preventDefault();
    try {
      sessionStorage.setItem('testrome.reloadedForUpdate', String(Date.now()));
    } catch { /* storage may be blocked */ }
    window.location.reload();
  });
}

/** For tests. */
export function __resetErrorReporting(): void {
  sent.clear();
}
